import type { Client } from '@/types';
import { getCalendarDateKey } from '@/shared/lib/calendarDate';
import { getPassMeta } from '@/shared/lib/client';
import { searchByName } from '@/shared/lib/search';
import type { SortDirection } from '@/shared/lib/sort';

export const clientSortFieldValues = ['name', 'endDate'] as const;
export type ClientSortField = (typeof clientSortFieldValues)[number];
export type ClientSortDirection = SortDirection;
export type ClientFilterType = 'all' | 'active' | 'expiringSoon' | 'expired';

export const isExpiringSoon = (client: Client): boolean =>
  getPassMeta(client.pass)?.isEndingSoon ?? false;

export const filterClients = (clients: Client[], filterBy: ClientFilterType): Client[] => {
  if (filterBy === 'all') {
    return clients;
  }

  return clients.filter((client) => {
    const meta = getPassMeta(client.pass);

    if (filterBy === 'active') {
      return meta?.isActive ?? false;
    }

    if (filterBy === 'expiringSoon') {
      return meta?.isEndingSoon ?? false;
    }

    if (filterBy === 'expired') {
      return meta?.isExpired || meta?.isOutOfCredits || false;
    }

    return false;
  });
};

const FAR_FUTURE_DATE_KEY = '9999-12-31';

export const sortClients = (
  clients: Client[],
  sortBy: ClientSortField,
  direction: ClientSortDirection
): Client[] => {
  const sign = direction === 'desc' ? -1 : 1;

  return [...clients].sort((a, b) => {
    if (sortBy === 'name') {
      return sign * a.name.localeCompare(b.name);
    }

    if (sortBy === 'endDate') {
      const dateA = a.pass?.endsAt ? getCalendarDateKey(a.pass.endsAt) : FAR_FUTURE_DATE_KEY;
      const dateB = b.pass?.endsAt ? getCalendarDateKey(b.pass.endsAt) : FAR_FUTURE_DATE_KEY;
      return sign * dateA.localeCompare(dateB);
    }

    return 0;
  });
};

export const searchClientsByName = (clients: Client[], query: string): Client[] =>
  searchByName(clients, query);
